/**
 * Location-aware date formatting.
 *
 * A single source of truth for how dates are shown across the platform.
 * The active hotel's country (falling back to timezone) decides the locale,
 * which decides the ordering (DD/MM/YYYY vs MM/DD/YYYY vs YYYY/MM/DD) and the
 * month/day names — automatically, via the browser's Intl engine.
 *
 * Usage in a component:
 *   const { formatDate, formatDateTime, patternHint } = useDateFormat();
 *   <span>{formatDate(row.date)}</span>
 *
 * Values may be ISO date strings ("2026-08-24"), ISO datetimes, or Date
 * objects. Date-only strings are parsed in LOCAL time so they never slip a
 * day due to UTC midnight.
 */
import { useMemo } from "react";
import { useHotelContext } from "@/contexts/HotelContext";

const DEFAULT_LOCALE = "en-GB"; // neutral DD/MM/YYYY when nothing is known

// Country name / ISO code → BCP-47 locale. Extend freely.
const COUNTRY_LOCALE = {
  india: "en-IN", in: "en-IN", ind: "en-IN",
  "united states": "en-US", usa: "en-US", us: "en-US", "united states of america": "en-US",
  "united kingdom": "en-GB", uk: "en-GB", gb: "en-GB", "great britain": "en-GB", england: "en-GB",
  canada: "en-CA", ca: "en-CA",
  australia: "en-AU", au: "en-AU",
  "new zealand": "en-NZ", nz: "en-NZ",
  ireland: "en-IE", ie: "en-IE",
  "united arab emirates": "en-AE", uae: "en-AE", ae: "en-AE",
  "saudi arabia": "en-SA", sa: "en-SA",
  qatar: "en-QA", qa: "en-QA",
  singapore: "en-SG", sg: "en-SG",
  malaysia: "en-MY", my: "en-MY",
  "hong kong": "en-HK", hk: "en-HK",
  "south africa": "en-ZA", za: "en-ZA",
  germany: "de-DE", de: "de-DE",
  france: "fr-FR", fr: "fr-FR",
  spain: "es-ES", es: "es-ES",
  italy: "it-IT", it: "it-IT",
  netherlands: "nl-NL", nl: "nl-NL",
  portugal: "pt-PT", pt: "pt-PT",
  brazil: "pt-BR", br: "pt-BR",
  mexico: "es-MX", mx: "es-MX",
  japan: "ja-JP", jp: "ja-JP",
  china: "zh-CN", cn: "zh-CN",
  "south korea": "ko-KR", kr: "ko-KR", korea: "ko-KR",
  thailand: "th-TH", th: "th-TH",
  indonesia: "id-ID", id: "id-ID",
  turkey: "tr-TR", tr: "tr-TR",
};

// Timezone → locale fallback when country is missing.
const TZ_LOCALE = [
  [/^Asia\/(Kolkata|Calcutta)/i, "en-IN"],
  [/^Asia\/Dubai/i, "en-AE"],
  [/^Asia\/Singapore/i, "en-SG"],
  [/^Asia\/Tokyo/i, "ja-JP"],
  [/^Asia\/Shanghai/i, "zh-CN"],
  [/^Australia\//i, "en-AU"],
  [/^Europe\/London/i, "en-GB"],
  [/^Europe\/Paris/i, "fr-FR"],
  [/^Europe\/Berlin/i, "de-DE"],
  [/^Europe\/Madrid/i, "es-ES"],
  [/^Europe\//i, "en-GB"],
  [/^America\/(New_York|Chicago|Denver|Los_Angeles|Phoenix|Anchorage)/i, "en-US"],
  [/^America\/(Toronto|Vancouver|Edmonton|Winnipeg)/i, "en-CA"],
];

export function localeFromContext(ctx) {
  if (!ctx) return DEFAULT_LOCALE;
  const country = (ctx.country || "").trim().toLowerCase();
  if (country && COUNTRY_LOCALE[country]) return COUNTRY_LOCALE[country];
  const tz = ctx.timezone || "";
  if (tz) {
    for (const [re, loc] of TZ_LOCALE) if (re.test(tz)) return loc;
  }
  return DEFAULT_LOCALE;
}

export function parseDateValue(value) {
  if (!value) return null;
  if (value instanceof Date) return isNaN(value.getTime()) ? null : value;
  const s = String(value);
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (m) return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  const d = new Date(s);
  return isNaN(d.getTime()) ? null : d;
}

/** Derive a human "DD/MM/YYYY"-style hint for a locale (for input placeholders). */
export function patternHintFor(locale) {
  try {
    const parts = new Intl.DateTimeFormat(locale, {
      year: "numeric", month: "2-digit", day: "2-digit",
    }).formatToParts(new Date(2026, 11, 31));
    return parts
      .map((p) => {
        if (p.type === "day") return "DD";
        if (p.type === "month") return "MM";
        if (p.type === "year") return "YYYY";
        return p.value;
      })
      .join("");
  } catch {
    return "DD/MM/YYYY";
  }
}

export function makeFormatters(locale, timeZone) {
  const tzOpt = timeZone ? { timeZone } : {};
  const dateFmt = new Intl.DateTimeFormat(locale, {
    day: "2-digit", month: "short", year: "numeric",
  });
  const dateNumericFmt = new Intl.DateTimeFormat(locale, {
    day: "2-digit", month: "2-digit", year: "numeric",
  });
  const dateWeekdayFmt = new Intl.DateTimeFormat(locale, {
    weekday: "short", day: "2-digit", month: "short", year: "numeric",
  });
  // Time-bearing formatters honour the hotel timezone so timestamps (stored
  // in UTC) render in the property's local wall-clock time.
  const dateTimeFmt = new Intl.DateTimeFormat(locale, {
    day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit", ...tzOpt,
  });
  const timeFmt = new Intl.DateTimeFormat(locale, { hour: "2-digit", minute: "2-digit", ...tzOpt });
  const monthDayFmt = new Intl.DateTimeFormat(locale, { day: "2-digit", month: "short" });

  const safe = (fmt, value, empty) => {
    const d = parseDateValue(value);
    return d ? fmt.format(d) : empty;
  };

  return {
    locale,
    patternHint: patternHintFor(locale),
    /** e.g. 24 Aug 2026 */
    formatDate: (v, empty = "—") => safe(dateFmt, v, empty),
    /** e.g. 24/08/2026 — compact numeric, region-ordered */
    formatDateNumeric: (v, empty = "—") => safe(dateNumericFmt, v, empty),
    /** e.g. Mon, 24 Aug 2026 */
    formatDateWeekday: (v, empty = "—") => safe(dateWeekdayFmt, v, empty),
    /** e.g. 24 Aug 2026, 09:30 */
    formatDateTime: (v, empty = "—") => safe(dateTimeFmt, v, empty),
    /** e.g. 09:30 */
    formatTime: (v, empty = "—") => safe(timeFmt, v, empty),
    /** e.g. 24 Aug */
    formatMonthDay: (v, empty = "—") => safe(monthDayFmt, v, empty),
  };
}

/** React hook — reads the active hotel context and returns memoized formatters. */
export function useDateFormat() {
  const hc = useHotelContext();
  const ctx = hc?.ctx;
  const locale = localeFromContext(ctx);
  const timeZone = ctx?.timezone || undefined;
  return useMemo(() => makeFormatters(locale, timeZone), [locale, timeZone]);
}
